/**
 * Features-step-specific literals.
 *
 * The raw colour / gradient values that ONLY the Features step (step 2) uses,
 * ported from the standalone app's `features/features/*` components and the two
 * animation CSS files (`highlights.css`, `package-chooser.css`). They re-use the
 * shared `CFG` palette where possible so this file holds only what is genuinely
 * features-local, and so the step's styled-components carry no raw literals.
 *
 * NOTE: per-tag `logo.bg` / `logo.color` (highlight pills) are DATA values
 * (source-brand colours) and arrive as props — they are not here.
 */
import { themed } from '../../../../theme/themeRegistry';
import { CFG } from '../../../../theme/configurator-tokens';

// FeatureCard / LevelTabs -----------------------------------------------------
export const FEAT = themed('cfgFeat', {
  /** Feature card body surface. */
  cardBg: '#17171a',
  /** Card hairline when not focused. */
  cardBorder: 'rgba(255, 255, 255, 0.07)',
  /** Icon tile gradient (dark wash over zinc). */
  iconTile:
    'linear-gradient(0deg, rgba(0,0,0,0.24), rgba(0,0,0,0.24)),' +
    'linear-gradient(0deg, #18181b, #18181b)',
  /** Focused-card orange edge (same as the wizard's brand fill). */
  focusedEdge: 'linear-gradient(180deg, #ff9a4d 0%, #ff791b 42%, rgba(255,121,27,0.25) 100%)',
  // Level tabs.
  tabTrack: '#1d1d1f',
  tabBorderFocused: 'rgba(255, 121, 27, 0.64)',
  tabBorderUnfocused: 'rgba(255, 255, 255, 0.2)',
  tabTextIdle: 'rgba(255, 255, 255, 0.6)',
  tabTextHover: 'rgba(255, 255, 255, 0.9)',
  /** Hover fill on a tier button while its card is focused — the per-tier hover indicator. */
  tabHoverBg: 'rgba(255, 255, 255, 0.06)',
  /** Disabled (campaign doesn't price/enable this tier) — dimmed + struck label. */
  tabTextDisabled: 'rgba(255, 255, 255, 0.28)',
  /** Selected-tab label colour when the parent card is focused (brand orange).
   *  Uses brandText so it deepens to an AA burnt-orange on the light tab. */
  tabTextFocusedSelected: CFG.brandText,
  /** Selected-tab label colour when the parent card is NOT focused (white). */
  tabTextUnfocusedSelected: CFG.foreground,
} as const);

// FeaturesTotal ---------------------------------------------------------------
export const TOTAL = themed('cfgFeatTotal', {
  /** "Saved N%" green. */
  saved: CFG.success,
} as const);

// SwitchToCustomModal — the "Switch to Custom?" confirm dialog (Figma 4861:5696),
// shown when a tier click on a committed pack would drop to Custom pricing. ------
export const CONFIRM = themed('cfgFeatConfirm', {
  /** Centered alert-dialog surface (Figma surface/surface). */
  surface: '#18181b',
  /** Backdrop scrim behind the dialog. */
  backdrop: 'rgba(0, 0, 0, 0.6)',
  /** Layered drop shadow + 1px inner top-light (Figma shadow-overlay). */
  shadow:
    '0px 2px 4px rgba(0, 0, 0, 0.06), 0px -6px 6px rgba(0, 0, 0, 0.03),' +
    ' 0px 14px 14px rgba(0, 0, 0, 0.08), inset 0px 0px 1px rgba(255, 255, 255, 0.3)',
  /** Avatar + Cancel button surface (zinc-800). */
  chip: CFG.base100,
  /** Avatar icon colour. */
  icon: CFG.foreground,
  /** Title gradient (purple → grey → tan), clipped to text. */
  titleGradient: 'linear-gradient(90deg, #aea6f1 0%, #cec1cc 50%, #da9c74 100%)',
  /** Body copy (muted). */
  bodyText: CFG.textMuted,
  /** Confirm button fill (brand). */
  confirmBg: CFG.brand,
  /** Confirm button label (dark, on brand). */
  confirmText: '#000000',
  /** Cancel button label (near-white, Figma default/default-foreground). */
  cancelText: '#fcfcfc',
} as const);

// PackageCard ("Find my pack" promo, Lumio) -----------------------------------
// NOT theme-wrapped: this is an intentional deep-space brand card that stays
// dark in BOTH themes, so its text must stay fixed-white (the shared CFG.* inks
// would flip to dark on light and go dark-on-dark-purple — see PackageCard).
export const PROMO = {
  /** Deep-space purple background gradient. */
  cardBg: 'linear-gradient(247.6deg, #010039 0%, #0f0156 49%, #6a26f6 100%)',
  /** Cyan CTA border. */
  ctaBorder: '#00f0ff',
  /** Iridescent CTA fill (cyan → magenta, low alpha). */
  ctaFill:
    'linear-gradient(90deg, rgba(0,240,255,0.16) 0%, rgba(0,224,255,0.16) 32%,' +
    'rgba(128,112,233,0.16) 62%, rgba(255,0,212,0.16) 100%)',
  /** Fixed-white text on the always-dark card (theme-stable). */
  title: '#ffffff',
  blurb: '#d4d4d8',
  ctaText: '#ffffff',
} as const;

// SelectedPackCard — gem-style cards (3 real packs + the green Basic baseline) ----
// The card is a horizontal gradient with the crystal "gem" art baked into the
// right 64% PNG (`pack-sel-*-gem.png`, exported from the Figma cards). Because the
// PNG bakes the same gradient, only the LEFT 36% of `gradient` is visible (behind
// the text), and the 70.356% / 101.63% stops keep the seam at 36% matching the PNG.
// Gradients are the EXACT Figma values — Focused 7838:37261 / Edge 7838:37326 /
// Superstar 7838:37354 / Basic 8483:37349 (green).
export interface PackStyle {
  gradient: string;
  gem: string;
}

export const PACK_STYLES: PackStyle[] = [
  { gradient: 'linear-gradient(to right, #0082a2 0%, #001e3a 70.356%, #000d1a 101.63%)', gem: 'pack-sel-1-gem.png' },
  { gradient: 'linear-gradient(to right, #8d55e6 0%, #31003a 70.356%, #10001a 101.63%)', gem: 'pack-sel-2-gem.png' },
  { gradient: 'linear-gradient(to right, #ff2b00 0%, #3a0014 70.356%, #1a0000 101.63%)', gem: 'pack-sel-3-gem.png' },
];

// Basic baseline — the green gem card (Figma 8483:39349). Same structure as a pack.
export const BASIC_PACK_STYLE: PackStyle = {
  gradient: 'linear-gradient(to right, #00a259 0%, #003a34 70.356%, #00121a 101.63%)',
  gem: 'pack-sel-basic-gem.png',
};

// Custom — a SOLID-BLACK card with a rotated montage of pastel gamification-icon tiles on the
// right (Figma 8483:39380). The montage PNG (exported from node 8483:39394) bakes its own black
// background + 15° rotation. A subtle blurred white glow sits behind it (Figma light 8483:39393:
// a 390-radius pill, #FFF @ 11% opacity, blur 22.82px) so the surface isn't flat black.
export const CUSTOM_PACK = {
  background: '#000000',
  art: 'pack-sel-custom-grid.png',
  /** Soft background glow colour (Figma light 8483:39393). */
  glowColor: '#ffffff',
} as const;

export const SELECTED_PACK = {
  /** "Change pack" button border + fill. */
  buttonBorder: '#ffffff',
  buttonFill: 'rgba(255, 255, 255, 0.1)',
  /** Eyebrow / name / button label — fixed white on the always-dark gem & custom
   *  cards (the shared CFG ink flips to dark on light → dark-on-dark gem). */
  text: '#ffffff',
} as const;

// PackageChooser overlay — full-screen "Lumio" AI-scan (ported verbatim from the
// standalone app's `package-chooser.css`: a `fixed inset-0` modal with a centred
// `lumio-container`, an iridescent scan UI, then the pack grid). -----------------
export const CHOOSER = themed('cfgFeatChooser', {
  /** Backdrop scrim behind the full-screen modal (blurred). */
  backdrop: 'rgba(0, 0, 0, 0.6)',
  /** Centred modal container surface + chrome. */
  containerBg: '#09090b',
  containerBorder: 'rgba(255, 255, 255, 0.08)',
  containerShadow: '0 40px 100px rgba(0, 0, 0, 0.8)',
  headerBorder: 'rgba(255, 255, 255, 0.06)',
  /** Frosted-glass fill for the modal's docked header — darker + more opaque than the
   *  sidebar's shared `dockGlassBg` (which stays translucent at 0.2). */
  headerGlassBg: 'rgba(9, 9, 12, 0.45)',
  /** Small scrims that fade scrolled body content into the docked header / footer
      (container surface → transparent), mirroring the mobile header/bottom fades. */
  scrimHeader: 'linear-gradient(to bottom, #09090b 0%, rgba(9, 9, 11, 0) 100%)',
  scrimFooter: 'linear-gradient(to top, #09090b 0%, rgba(9, 9, 11, 0) 100%)',
  /** Lumio wordmark gradient (cyan → magenta). */
  lumioText: 'linear-gradient(90deg, #00f0ff 0%, #00e0ff 32%, #8070e9 62%, #ff00d4 100%)',
  /** Lumio sparkle-star logo / open-intro "hero star": base outline + body fill. */
  lumioStarStroke: 'rgba(255, 255, 255, 0.15)',
  lumioStarFill: 'rgba(255, 255, 255, 0.03)',
  /** Iridescent star/wave gradient stops (pink → cyan). */
  lumioGradFrom: '#ff00d4',
  lumioGradTo: '#00e0ff',
  /** Hero-star travel glow (magenta / cyan drop-shadows) + the no-glow keyframe stop. */
  starGlowMagenta: 'rgba(192, 38, 211, 0.4)',
  starGlowMagentaSoft: 'rgba(192, 38, 211, 0.2)',
  starGlowCyan: 'rgba(6, 182, 212, 0.2)',
  starGlowNone: 'rgba(0, 0, 0, 0)',
  /** Header bottom hairline — transparent until the open intro fades it in. */
  headerBorderHidden: 'rgba(255, 255, 255, 0)',
  /** Placeholder/shimmer pack card surface (scan phase). */
  cardBg: 'rgba(22, 22, 25, 0.7)',
  cardBorder: 'rgba(255, 255, 255, 0.04)',
  cardShimmer: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)',
  /** Section / pack-grid title. */
  packsTitle: '#fafafa',
  /** Status row: uppercase label + the gradient status text + percentage. */
  statusLabel: '#71717a',
  statusText: 'linear-gradient(to right, #ff00d4, #00e0ff, #ff00d4)',
  statusDot: 'linear-gradient(90deg, #ff00d4 0%, #00e0ff 100%)',
  /** Scan progress track + iridescent (pink → cyan) fill. */
  scanTrack: 'rgba(22, 22, 25, 0.7)',
  scanTrackBorder: 'rgba(255, 255, 255, 0.03)',
  scanFill:
    'linear-gradient(90deg, #ff00d4 0%, #00e0ff 40%, rgba(255,255,255,0.8) 50%, #00e0ff 60%, #ff00d4 100%)',
  /** Competitor pill surfaces + the green settled tick. */
  competitorTitle: '#8e8e93',
  pillBg: '#151518',
  pillBorder: 'rgba(255, 255, 255, 0.04)',
  pillName: '#ffffff',
  pillLogoBg: '#ffffff',
  pillTick: CFG.success,
  pillLoader: '#8e8e93',
  /** Opaque fill for the pill border-beam mask (white = ring visible, centre cut out). */
  pillBeamMask: '#ffffff',
  /** "+N more" accumulation counter. */
  moreCounter: '#e4e4e7',
  /** Settled-phase pack cards. */
  packBg: 'rgba(22, 22, 25, 0.7)',
  packBorder: 'rgba(255, 255, 255, 0.04)',
  packBorderSelected: '#ff791b',
  packGlow: '0 12px 48px -10px rgba(255, 121, 27, 0.26)',
  /** Selected pack card: soft orange bloom growing from the BOTTOM-RIGHT corner (mirrors the
   *  Figma spec, node 9086:3818). A pure-CSS elliptical radial-gradient — % positioning keeps
   *  it pinned bottom-right at any card width, and it reads stronger/cleaner than the old SVG. */
  packGlowGradient:
    'radial-gradient(68% 120% at 99% 96%,' +
    ' rgba(255, 121, 27, 0.5) 0%,' +
    ' rgba(255, 121, 27, 0.28) 26%,' +
    ' rgba(170, 78, 18, 0.12) 50%,' +
    ' rgba(255, 121, 27, 0) 72%)',
  /** BorderGlow mesh palette for the pack cards — the standard `BorderGlow` primitive's
   *  three-stop colour border (white -> brand orange -> Lumio purple). Drives the recommended
   *  pack's one-shot intro sweep and the hover glow on any non-selected card. */
  packGlowArc: ['#ffffff', '#f97316', '#a855f7'],
  /** Halo + inner-glow colour for the pack BorderGlow — primary brand orange (matches the
   *  selected pack's orange edge), not white, so the glow reads on-brand. */
  packGlowColor: '#ff791b',
  /** Hover mesh for the SELECTED pack — solid primary orange (the border stays solid, not
   *  iridescent, to match the selected card's solid orange edge). */
  packGlowArcSelected: ['#ff791b', '#ff791b', '#ff791b'],
  /** Opaque surface the BorderGlow uses to mask its mesh down to the border ring — the
   *  pack card's `packBg` composited over the modal, so the card centre stays seamless
   *  (a translucent value would bleed mesh colour into the centre). */
  packGlowSurface: '#131316',
  packName: '#ffffff',
  packNameSelected: '#ff791b',
  packDesc: '#8e8e93',
  /** "Recommended" tag — Lumio-coloured pill (Figma 9107:5068): an iridescent radial wash
   *  (magenta core → blue rim) @20% over a doubled 4% white lift, a #ff00d4 hairline, and the
   *  label as a magenta→cyan gradient (@75%) over a #d4d4d8 base, clipped to the text. The radial
   *  ellipse fills the whole pill (h-radius = ½ width, v-radius = ½ height), so the wash reads as
   *  a horizontal magenta→blue band. The magenta/cyan are the Lumio brand stops. */
  recoTagBg:
    'radial-gradient(ellipse 50% 50% at 50% 50%,' +
    ' rgba(154, 40, 170, 0.2) 0%, rgba(132, 63, 178, 0.2) 25%,' +
    ' rgba(110, 86, 186, 0.2) 50%, rgba(66, 132, 201, 0.2) 100%),' +
    ' linear-gradient(0deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.04)),' +
    ' linear-gradient(0deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.04))',
  recoTagBorder: '#ff00d4',
  recoTagText:
    'linear-gradient(165.03deg, rgba(255, 0, 212, 0.75) 19.6%, rgba(0, 224, 255, 0.75) 85.58%),' +
    ' linear-gradient(90deg, #d4d4d8 0%, #d4d4d8 100%)',
  /** Selected-pack check badge (Figma 3718:26466 / 3821:11860): a 22px orange circle with a
   *  black tick — the same orange/dark pairing as the confirm CTA. */
  checkBg: '#ff791b',
  checkMark: '#000000',
  /** Old/struck price + save badge. */
  oldPrice: '#52525b',
  saveBadge: CFG.success,
  /** Included-feature pill ("feature pack includes"). */
  featurePillBg: 'rgba(22, 22, 25, 0.7)',
  featurePillBorder: 'rgba(255, 255, 255, 0.05)',
  fpTitle: '#ffffff',
  fpTitleAccent: '#ff791b',
  /** Footer summary bar + confirm CTA. */
  footerBg: '#121214',
  footerBorder: 'rgba(255, 255, 255, 0.05)',
  footerName: '#ff791b',
  footerDivider: '#52525b',
  footerPrice: '#ffffff',
  confirmBg: '#ff791b',
  confirmText: '#000000',
  /** Detailed feature view ("See detailed view") — You-vs-Market bars, styled like v2. */
  fpTrackBg: 'rgba(255, 255, 255, 0.05)',
  /** Detailed-view "You" bar fill — a deeper blue gradient, intentionally DISTINCT from the
   *  Package-benchmark panel's cyan BENCHMARK.youFill, so the detailed bars read apart from it. */
  fpYouFill: 'linear-gradient(109deg, #172D9E 12.87%, #787CFE 87.13%)',
  /** Market "range" hatch — same logic/colours as the Package-benchmark panel: grey when it
   *  sits BEHIND the You fill (Market > You, shows only in the You→Market gap)... */
  fpMarketHash:
    'repeating-linear-gradient(-45deg, rgba(142, 142, 147, 0.4) 0px, rgba(142, 142, 147, 0.4) 2px, transparent 2px, transparent 6px)',
  /** ...and WHITE when it overlays the You fill (You ≥ Market), which reads over this bar's deep
   *  navy→periwinkle fill. Deliberately NOT the same ink as BENCHMARK.marketHashOver: that panel's
   *  fill is light cyan, so its overlay hatch is near-black instead. Both are theme-invariant. */
  fpMarketHashOver:
    'repeating-linear-gradient(-45deg, rgba(255, 255, 255, 0.5) 0px, rgba(255, 255, 255, 0.5) 2px, transparent 2px, transparent 6px)',
  fpTierBg: '#121214',
  /** Detailed-view tier/level pill label — white (was cyan). */
  fpTierAccent: '#ffffff',
  /** Compact detailed-row icon TILE surface (Figma 9049-3933) — the bigger 66px icon sits on a
   *  dark rounded tile in the stacked layout. */
  detailIconBg: '#18181b',
} as const);

// Package-benchmark panel — the "You vs Market" indicator shown after the scan (Figma
// 8447-4809/4680/5075/5205). Pixel-perfect: track 738px ref, Market fill 410px (55.6%),
// "You" orange fill per pack. Colours are gradient/value tokens (no raw literals in components).
export const BENCHMARK = themed('cfgFeatBenchmark', {
  /** Panel surface: lighter-than-modal card + a dark overlay + a faint purple glow, 0.5px edge. */
  panelBg: '#18181b',
  panelOverlay: 'rgba(0, 0, 0, 0.32)',
  panelGlow:
    'radial-gradient(120px 90px at 22% 0%, rgba(125, 113, 233, 0.20) 0%, rgba(94, 85, 175, 0.10) 45%, rgba(0, 0, 0, 0) 75%)',
  /** Left block: "PACKAGE BENCHMARK" eyebrow + the cyan market-level caption. */
  eyebrow: '#888889',
  caption: '#0cc3e4',
  /** Track + fills. */
  track: '#27272a',
  /** "You" fill — cyan→blue gradient. The detailed feature bars deliberately use a deeper-blue
   *  CHOOSER.fpYouFill instead, so the benchmark panel and the detailed view read apart. */
  youFill: 'linear-gradient(90deg, #00e0ff 0%, #0099ff 100%)',
  /** Market "range" segment (0 → market%): grey diagonal hatch, mirroring the detailed
   *  feature-view You-vs-Market bars (FEATURES.fpMarketHash). Used when it sits BEHIND the
   *  orange You fill (You < Market) — grey reads over the dark track in the You→Market gap. */
  marketHash:
    'repeating-linear-gradient(-45deg, rgba(142, 142, 147, 0.4) 0px, rgba(142, 142, 147, 0.4) 2px, transparent 2px, transparent 6px)',
  /** Variant used when the market hatch OVERLAYS the You fill (You ≥ Market). Near-black, in BOTH
   *  themes: this panel's fill is the light cyan→blue gradient, which a white hatch washes out. The
   *  fill is theme-invariant, so the ink over it is pinned to this same value for light
   *  (themePalette LIGHT_OVERRIDES, guarded by theme/marketBarInk.test.ts). The detailed feature
   *  view keeps a WHITE hatch instead — its fill is the deep navy→periwinkle gradient. */
  marketHashOver:
    'repeating-linear-gradient(-45deg, rgba(17, 19, 26, 0.30) 0px, rgba(17, 19, 26, 0.30) 2px, transparent 2px, transparent 6px)',
  /** "You vs Market" label, You/Market marker text + triangles. */
  marker: '#a1a1aa',
  /** Right block: "Analyzed competitor" label, mini-logo pill surface + border, count text. */
  miniLabel: '#a1a1aa',
  miniPillBg: '#141417',
  miniLogoBorder: 'rgba(255, 255, 255, 0.07)',
  miniCount: '#ffffff',
  /** Pack-name pill riding the "You" fill tip — mirrors CHOOSER.fpTierBg / fpTierAccent (the
   *  detailed feature-view DetailTierPill). Shows the selected pack name (e.g. "Basic"). */
  tierBg: '#121214',
  /** Pack-name pill label — white (was cyan). */
  tierAccent: '#ffffff',
} as const);

// PackageChooser scan timing — a declarative re-port of the standalone app's
// 740-line imperative timeline (`package-chooser-timeline.ts`). A fixed schedule
// drives the phase + percentage waypoints + the competitor-pill stream; everything
// visual derives from this discrete state. Timings (ms). The scan phase visually
// settles around 39% with four expanded pills + a "+N more" counter (the
// reference screenshot), then completes and reveals the pack grid.
export const CHOOSER_SCAN = {
  /** Status-text trailing-dot interval. */
  dotIntervalMs: 420,
  /**
   * Progress waypoints `[seconds, 0–1 fraction]` — continuous, uneven pace. The
   * competitor scan settles around 39% (the reference snapshot), then the
   * "filter / prioritize / build" wrap-up carries it to 100%.
   */
  waypoints: [
    [0, 0],
    [0.4, 0.05],
    [0.8, 0.12],
    [1.2, 0.19],
    [1.6, 0.26],
    [2.0, 0.32],
    [2.4, 0.39],
    [2.9, 0.49],
    [3.3, 0.62],
    [3.7, 0.78],
    [4.0, 0.9],
    [4.3, 1],
  ] as ReadonlyArray<readonly [number, number]>,
  /**
   * Open-intro duration (ms): the hero-star journey + waves glow + staggered
   * header/scan-UI reveal play first, while the status sits on "Preparing AI
   * research agents…". The scan schedule below (percentage, pills, completion)
   * is offset by this so the bar only starts climbing once the intro lands.
   */
  introMs: 2900,
  /** When the percentage / progress bar starts (preparing → analyzing), after the intro. */
  analyzeAtMs: 350,
  /**
   * Competitor pill arrivals (ms). Only ~half the real competitors stream as pills
   * (the chooser slices the stream to `pillTimes.length`); the full set still backs the
   * benchmark "+N others" total. Spread across the same window at roughly double the old
   * spacing so the buildup reads as a deliberate scan, not a fast-forward.
   */
  pillTimes: [400, 640, 880, 1120, 1360, 1600, 1840, 2080, 2320, 2560] as const,
  /** Up to this many newest pills stay expanded; older ones collapse to round logos. */
  expandedPills: 4,
  /** Up to this many round logo chips show after the expanded group; the rest fold into "+N more". */
  collapsedChips: 3,
  /** Scan completes (reveal the pack grid) at this mark. */
  doneAtMs: 4400,
  /**
   * A competitor pill is removed this long (ms) after it starts exiting — the pill exit
   * CSS transition is 500ms, plus a 20ms buffer so the node is dropped only once the fade
   * has fully finished. Keep ≥ the CSS duration so they can't visibly desync.
   */
  pillExitMs: 520,
  /**
   * Each competitor pill spins ("analyzing", border beam + spinner) for this long after it
   * arrives, then settles to a green check. Shorter than the time a pill takes to demote out
   * of the expanded window, so the older expanded pills show a check while the newest still
   * spin — several analyze concurrently and resolve one-by-one (v2 market-scan feel).
   */
  pillAnalyzeMs: 450,
  /** Post-scan reveal step delays (ms): hold at 100% → reveal packs → reveal benchmark. */
  revealHoldMs: 1000,
  revealPacksMs: 850,
  revealBenchmarkMs: 950,
} as const;

// HighlightsExperience (page-local copy of the reveal experience) -------------
// Mirrors the Players step's `HL` block; ported from `highlights.css` +
// `reveal.ts`. Kept here so the Features copy of HighlightsExperience imports no
// cross-step token.
export const HL = themed('cfgFeatHl', {
  cardBg: '#111014',
  pillBg: '#2c2c2e',
  /** Apple-ease for the pill <-> expanded card morph. */
  appleEase: 'cubic-bezier(0.32, 0.72, 0, 1)',
  descText: '#cccccc',
  descShine: 'linear-gradient(120deg, #ccc 0%, #ccc 40%, #fff 50%, #ccc 60%, #ccc 100%)',
  groupTitle: '#8e8e93',
  loadingBorder:
    'conic-gradient(from 0deg, transparent 0deg, transparent 60deg, #00e0ff 120deg, #8070e9 180deg, #ff00d4 240deg, transparent 360deg)',
  checkGreen: CFG.success,
  checkWhite: '#ffffff',
  stack1: '#e72b02',
  stack2: '#222222',
  stack3: '#1d2b44',
  searchingText: CFG.base400,
  searchingFinal: '#ffffff',
  stageGlow: 'radial-gradient(circle at center, rgba(255,123,0,0.04) 0%, transparent 60%)',
  videoShadow: '0 30px 80px -20px rgba(0, 0, 0, 0.6)',
  pillLogoFallback: '#ffffff',
} as const);

/** No-video centre: a solid black panel shown instead of the demo clip when the
 *  active highlight has no `video_url`. Theme-invariant black (like the contained
 *  clip's letterbox, which stays black in light too). */
export const STAGE_BLACK_FILL = '#000000';

/** Equal edge-fade size (px) applied on all four sides of the demo clip / no-video
 *  panel so its black edges dissolve into the page. */
export const VIDEO_EDGE_FADE_PX = 80;

/** 4-side equal edge fade as an ALPHA mask (theme-invariant — black = opaque,
 *  transparent = cut out). Applied as BOTH `mask` and `-webkit-mask` with
 *  `intersect` / `source-in` compositing so corners fade on both axes; it only
 *  visibly acts where the content actually reaches an edge (object-fit letterbox
 *  areas are already transparent), i.e. it adapts to the clip's rendered size. */
export const VIDEO_EDGE_MASK =
  `linear-gradient(to right, transparent 0, #000 ${VIDEO_EDGE_FADE_PX}px, #000 calc(100% - ${VIDEO_EDGE_FADE_PX}px), transparent 100%),` +
  `linear-gradient(to bottom, transparent 0, #000 ${VIDEO_EDGE_FADE_PX}px, #000 calc(100% - ${VIDEO_EDGE_FADE_PX}px), transparent 100%)`;

/* ---- Light-theme demo-video CARD (light only) ----
 * On light the clip drops its edge feather and instead reads as a solid rounded card
 * (VideoStage `Frame`). These are theme-invariant literals (like the mask/black fill
 * above) because they are consumed only inside a `:root[data-ows-theme='light'] &`
 * block — never derived. Dark keeps the feathered, shadow-less clip unchanged. */

/** Soft drop shadow that grounds the black card on the light page (understated: a wide
 *  ambient pool + a tight contact shadow, in the palette's near-black ink). */
export const VIDEO_CARD_LIGHT_SHADOW =
  '0 24px 64px -28px rgba(17, 19, 26, 0.38), 0 6px 16px -8px rgba(17, 19, 26, 0.18)';

/** "Light bounce" overlay laid over the black card for physicality: a soft PASTEL blue
 *  glow from the top-left + a warm orange glow from the bottom-right, as if ambient light
 *  reflected off a glossy surface. Low alpha so it reads as a sheen, not a wash. */
export const VIDEO_CARD_LIGHT_BOUNCE =
  'radial-gradient(72% 92% at 10% 6%, rgba(130, 175, 255, 0.32) 0%, rgba(130, 175, 255, 0) 52%),' +
  'radial-gradient(78% 98% at 92% 98%, rgba(255, 176, 120, 0.28) 0%, rgba(255, 176, 120, 0) 56%)';

/** Reveal timings (ms) — ported from `features/features/reveal.ts`. */
export const HL_TIMING = {
  preType: 805,
  descCharMs: 17,
  pillCharMs: 23,
  groupPillPre: 288,
  groupPillPost: 115,
  tagPillPre: 345,
  tagPillPost: 173,
  searchStep: 805,
  searchSettle: 345,
  /**
   * Autoplay dwell base (ms): during autoplay a highlight stays active for this
   * PLUS the lesser of its video length (`video_duration_s`) and its text-reveal
   * duration, then the sequence advances to the next highlight.
   */
  autoAdvanceBaseMs: 2000,
} as const;

/** Searching-pill "+N more" counts shown in sequence (rendered as "+{{n}} more"). */
export const HL_SEARCH_STEPS = [3, 20, 77, 114] as const;
/** Final "+N more" count after the searching sequence settles. */
export const HL_SEARCH_FINAL = 114;
